import React, { Fragment, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getBattlebotSlotCenterPixels } from '../components/BattlebotPortalOverlay';
import './PulseShockwaveEffect.css';

/**
 * Double vertical shockwave — `pulse`: purple outer / white inner + purple damage numbers.
 * `culling`: dark red outer / light red inner, no floating damage numbers (strategem 51).
 * `swarm`: black/red outer + red inner + red damage numbers (strategem 57).
 * @param {'up'|'down'} direction — up = bottom→top (caster is viewer); down = top→bottom (opponent cast)
 */
export function PulseShockwaveEffect({
  direction = 'up',
  viewerHits,
  animationKey,
  variant = 'pulse',
}) {
  const [slotPoints, setSlotPoints] = useState(null);

  useLayoutEffect(() => {
    if (variant === 'culling') {
      setSlotPoints([]);
      return undefined;
    }
    let cancelled = false;
    const measure = (attempt) => {
      if (!viewerHits?.length) {
        if (!cancelled) setSlotPoints([]);
        return;
      }
      const pts = viewerHits.map((h, i) => {
        const pt = getBattlebotSlotCenterPixels(h.zone, h.index);
        return pt
          ? { ...pt, damage: h.damage, key: `${animationKey}-dmg-${i}` }
          : null;
      });
      if (pts.every(Boolean)) {
        if (!cancelled) setSlotPoints(pts);
        return;
      }
      if (attempt < 12 && !cancelled) {
        requestAnimationFrame(() => {
          if (!cancelled) measure(attempt + 1);
        });
      } else if (!cancelled) {
        setSlotPoints(pts.filter(Boolean));
      }
    };
    setSlotPoints(null);
    measure(0);
    return () => {
      cancelled = true;
    };
  }, [viewerHits, animationKey, variant]);

  const dirClass =
    direction === 'down' ? 'pulse-shockwave-dir-down' : 'pulse-shockwave-dir-up';

  const rootClass =
    variant === 'culling'
      ? 'pulse-shockwave-root pulse-shockwave-root--culling'
      : variant === 'swarm'
      ? 'pulse-shockwave-root pulse-shockwave-root--swarm'
      : 'pulse-shockwave-root';

  const bands = (
    <div className={rootClass} aria-hidden data-animation-key={animationKey}>
      <div className={`pulse-shockwave-travel ${dirClass}`}>
        <div className="pulse-shockwave-band-outer" />
        <div className="pulse-shockwave-band-inner" />
      </div>
    </div>
  );

  const dmgLayer = (
    <div className="pulse-shockwave-dmg-layer" aria-hidden data-animation-key={animationKey}>
      {(slotPoints || []).map((p, i) => (
        <div
          key={p.key}
          className={
            variant === 'swarm'
              ? 'pulse-shockwave-dmg pulse-shockwave-dmg--swarm'
              : 'pulse-shockwave-dmg'
          }
          style={{
            left: `${p.x}px`,
            top: `${p.y}px`,
            animationDelay: `${420 + i * 48}ms`,
          }}
        >
          {Math.round(Number(p.damage) || 0)}
        </div>
      ))}
    </div>
  );

  const showDamageLayer = variant !== 'culling';

  if (typeof document !== 'undefined' && document.body) {
    return (
      <Fragment>
        {createPortal(bands, document.body)}
        {showDamageLayer ? createPortal(dmgLayer, document.body) : null}
      </Fragment>
    );
  }
  return (
    <Fragment>
      {bands}
      {showDamageLayer ? dmgLayer : null}
    </Fragment>
  );
}

export default PulseShockwaveEffect;
